import { useState } from 'react';
import { toast } from 'sonner';
import { Send, FileText, RefreshCcw } from 'lucide-react';
import { Button } from '../ui/Button';
import { ConfirmModal } from '../ui/ConfirmModal';
import { useCurrentUser } from '../../hooks/useAuth';
import { api, ApiError } from '../../lib/api';

/**
 * Per-person "Re-send certificate" button. Hits the certificate
 * worker via pg-boss; the actual send happens asynchronously so the
 * UI confirms enqueue immediately. Visible to admin / coordinator.
 */
export function ResendCertificatePanel({
  personId,
  fullName,
  email,
}: {
  personId: string;
  fullName: string;
  email: string;
}) {
  const { data: user } = useCurrentUser();
  const [sending, setSending] = useState(false);
  const [resendOpen, setResendOpen] = useState(false);
  const allowed = user?.roles.some((r) =>
    ['admin', 'super_admin', 'coordinator'].includes(r),
  );
  if (!allowed) return null;
  async function resend() {
    setSending(true);
    try {
      await api(`/api/admin/certificates/${personId}/resend`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      toast.success(`Resend queued for ${fullName}`);
      setResendOpen(false);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'resend failed');
    } finally {
      setSending(false);
    }
  }
  return (
    <section className="rounded-lg border border-line/10 bg-surface p-5 shadow-sm sm:p-6">
      <div className="flex items-center gap-2">
        <Send className="h-4 w-4 text-info-soft-fg" />
        <h2 className="text-sm font-semibold">Re-send certificate</h2>
      </div>
      <p className="mt-1 text-xs text-fg-muted">
        Force a fresh send of the completion certificate PDF to{' '}
        <code className="font-mono">{email}</code>. Logs to the email log and
        appends a new CertificateSent event with via=resend.
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        <a
          href={`/api/admin/certificates/${personId}/pdf`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex h-8 items-center gap-1.5 rounded-md border border-line-strong/20 bg-surface px-3 text-xs font-medium text-fg-muted shadow-sm transition-colors hover:bg-surface-2"
        >
          <FileText className="h-4 w-4" />
          View certificate PDF
        </a>
        <Button
          variant="secondary"
          size="sm"
          iconLeft={<Send className="h-4 w-4" />}
          loading={sending}
          onClick={() => setResendOpen(true)}
        >
          Re-send certificate now
        </Button>
        <RegenerateCertificateButton personId={personId} fullName={fullName} />
      </div>

      <ConfirmModal
        open={resendOpen}
        tone="warning"
        icon={Send}
        title={`Re-send certificate to ${fullName}?`}
        message={
          <>
            They will receive a fresh PDF attachment at{' '}
            <code className="font-mono">{email}</code>. Logs to the email log
            and appends a new <code>CertificateSent</code> event with
            via=resend.
          </>
        }
        confirmLabel="Re-send now"
        cancelLabel="Cancel"
        onConfirm={resend}
        onClose={() => setResendOpen(false)}
      />
    </section>
  );
}

/**
 * Re-renders the snapshotted PDF for this person against the current
 * pdf_templates row, overwriting the bytes the download link serves.
 * Does not send another email. Useful when the wording of the cert
 * was updated after they completed.
 */
function RegenerateCertificateButton({
  personId,
  fullName,
}: {
  personId: string;
  fullName: string;
}) {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  async function go() {
    setBusy(true);
    try {
      await api(`/api/admin/certificates/${personId}/regenerate`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      toast.success(`Re-rendered for ${fullName}`);
      setOpen(false);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'regenerate failed');
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        iconLeft={<RefreshCcw className="h-4 w-4" />}
        loading={busy}
        onClick={() => setOpen(true)}
        title="Overwrite the saved PDF snapshot with a fresh render from the current template"
      >
        Re-render snapshot
      </Button>
      <ConfirmModal
        open={open}
        tone="warning"
        icon={RefreshCcw}
        title={`Re-render ${fullName}'s certificate?`}
        message="Overwrites the saved PDF with a fresh render from the current template. The download link starts serving the new copy. No email is sent."
        confirmLabel="Re-render"
        cancelLabel="Cancel"
        onConfirm={go}
        onClose={() => setOpen(false)}
      />
    </>
  );
}
